import type {
  ConferenceIndex,
  ConferenceTalkSummary,
  ReadingProgress,
} from '../../data/schema';

const SESSION_COLORS: Record<string, string> = {
  'sat-am': 'gold',
  'sat-pm': 'blue',
  'sun-am': 'green',
  'sun-pm': 'plum',
};

function routerLink(href: string, label: string, className?: string): HTMLAnchorElement {
  const link = document.createElement('a');
  link.href = href;
  link.dataset.router = '';
  link.textContent = label;
  if (className) link.className = className;
  return link;
}

function createHero(index: ConferenceIndex): HTMLElement {
  const hero = document.createElement('section');
  hero.className = 'home-hero';
  hero.setAttribute('aria-labelledby', 'conference-title');

  const inner = document.createElement('div');
  inner.className = 'home-hero__inner';
  const copy = document.createElement('div');
  copy.className = 'home-hero__copy';

  const kicker = document.createElement('p');
  kicker.className = 'eyebrow';
  kicker.textContent = `${index.dates} · Salt Lake City`;
  const title = document.createElement('h1');
  title.id = 'conference-title';
  title.textContent = index.title;
  const description = document.createElement('p');
  description.textContent =
    'Read every talk, follow guided connections, and study the themes that shape the conference.';
  copy.append(kicker, title, description);

  const stats = document.createElement('dl');
  stats.className = 'hero-stats';
  const statValues = [
    [String(index.talkCount), 'Talks'],
    [String(index.uniqueThemeCount), 'Themes'],
    [String(index.officialFootnoteCount), 'Published footnotes'],
  ];
  for (const [value, label] of statValues) {
    const stat = document.createElement('div');
    const term = document.createElement('dt');
    term.textContent = label;
    const detail = document.createElement('dd');
    detail.textContent = value;
    stat.append(detail, term);
    stats.append(stat);
  }

  inner.append(copy, stats);
  hero.append(inner);
  return hero;
}

function createContinueCard(
  talk: ConferenceTalkSummary,
  readingState: ReadingProgress | undefined,
): HTMLElement {
  const card = document.createElement('section');
  card.className = 'card continue-card';
  card.setAttribute('aria-labelledby', 'continue-title');

  const marker = document.createElement('span');
  marker.className = 'continue-card__marker';
  marker.setAttribute('aria-hidden', 'true');
  marker.textContent = '01';

  const copy = document.createElement('div');
  copy.className = 'continue-card__copy';
  const label = document.createElement('p');
  label.className = 'eyebrow eyebrow--gold';
  label.textContent = 'Continue reading';
  const title = document.createElement('h2');
  title.id = 'continue-title';
  title.textContent = talk.title;
  const meta = document.createElement('p');
  meta.className = 'muted';
  meta.textContent = `${talk.speaker.name} · ${talk.session.name}`;
  const progressDisplay = document.createElement('div');
  progressDisplay.className = 'start-progress';
  progressDisplay.setAttribute('aria-label', 'Reading progress: ready to begin');
  const progressBar = document.createElement('span');
  const progressFill = document.createElement('i');
  progressFill.style.width = `${String(readingState?.percent ?? 0)}%`;
  progressBar.append(progressFill);
  const progressText = document.createElement('small');
  progressText.textContent = readingState
    ? `${String(Math.round(readingState.percent))}% read`
    : 'Ready to begin';
  progressDisplay.setAttribute(
    'aria-label',
    `Reading progress: ${progressText.textContent}`,
  );
  progressDisplay.append(progressBar, progressText);
  copy.append(label, title, meta, progressDisplay);

  const action = routerLink(`/read/${talk.talkId}`, 'Open talk', 'button button--gold');
  card.append(marker, copy, action);
  return card;
}

function createTalkCard(
  talk: ConferenceTalkSummary,
  progress: ReadingProgress | undefined,
): HTMLElement {
  const card = document.createElement('article');
  card.className = 'talk-card';
  card.dataset.talkId = talk.talkId;

  const status = document.createElement('span');
  status.className = `status-chip${progress?.completed ? ' status-chip--done' : progress ? ' status-chip--reading' : ''}`;
  status.textContent = progress?.completed
    ? 'Read'
    : progress
      ? 'Reading'
      : 'Not started';

  const speaker = document.createElement('p');
  speaker.className = 'talk-card__speaker';
  speaker.textContent = talk.speaker.name;

  const title = document.createElement('h3');
  const link = routerLink(`/read/${talk.talkId}`, talk.title);
  title.append(link);

  const excerpt = document.createElement('p');
  excerpt.className = 'talk-card__excerpt';
  excerpt.textContent = talk.excerpt;

  const themes = document.createElement('ul');
  themes.className = 'tag-list';
  themes.setAttribute('aria-label', 'Top themes');
  for (const theme of talk.topThemes) {
    const item = document.createElement('li');
    item.textContent = theme.name;
    themes.append(item);
  }

  card.append(status, speaker, title, excerpt, themes);
  return card;
}

function createSessions(
  index: ConferenceIndex,
  progressByTalk: ReadonlyMap<string, ReadingProgress>,
): HTMLElement {
  const wrapper = document.createElement('section');
  wrapper.className = 'sessions';
  wrapper.setAttribute('aria-labelledby', 'sessions-title');

  const heading = document.createElement('div');
  heading.className = 'section-heading';
  const title = document.createElement('h2');
  title.id = 'sessions-title';
  title.textContent = `All ${String(index.talkCount)} talks`;
  const count = document.createElement('span');
  count.textContent = `${String(index.talkCount)} talks`;
  heading.append(title, count);
  wrapper.append(heading);

  const sessions = new Map<string, ConferenceTalkSummary[]>();
  for (const talk of index.talks) {
    const sessionTalks = sessions.get(talk.session.code) ?? [];
    sessionTalks.push(talk);
    sessions.set(talk.session.code, sessionTalks);
  }
  const orderedSessions = [...sessions.entries()].sort(
    ([, first], [, second]) => first[0].session.order - second[0].session.order,
  );

  for (const [sessionCode, talks] of orderedSessions) {
    const session = document.createElement('section');
    session.className = 'session-group';
    session.dataset.session = sessionCode;

    const sessionHeading = document.createElement('div');
    sessionHeading.className = 'session-heading';
    const marker = document.createElement('span');
    marker.className = `session-marker session-marker--${SESSION_COLORS[sessionCode] ?? 'gold'}`;
    marker.setAttribute('aria-hidden', 'true');
    const name = document.createElement('h3');
    name.textContent = talks[0].session.name;
    const talkCount = document.createElement('span');
    talkCount.textContent = `${String(talks.length)} talks`;
    sessionHeading.append(marker, name, talkCount);

    const grid = document.createElement('div');
    grid.className = 'talk-grid';
    for (const talk of talks) {
      grid.append(createTalkCard(talk, progressByTalk.get(talk.talkId)));
    }
    session.append(sessionHeading, grid);
    wrapper.append(session);
  }

  return wrapper;
}

function createQuickThreads(index: ConferenceIndex): HTMLElement {
  const card = document.createElement('section');
  card.className = 'card sidebar-card';
  card.setAttribute('aria-labelledby', 'threads-title');
  const title = document.createElement('h2');
  title.id = 'threads-title';
  title.textContent = 'Four reading threads';
  const description = document.createElement('p');
  description.className = 'muted';
  description.textContent = 'Start with the widest conference themes.';
  const list = document.createElement('ul');
  list.className = 'thread-list';

  for (const [position, theme] of index.featuredThemes.entries()) {
    const item = document.createElement('li');
    const link = routerLink(`/threads/${theme.slug}`, '');
    const number = document.createElement('span');
    number.className =
      `thread-list__number thread-list__number--${String(position + 1)}`;
    number.textContent = String(position + 1).padStart(2, '0');
    number.setAttribute('aria-hidden', 'true');
    const copy = document.createElement('span');
    const name = document.createElement('strong');
    name.textContent = theme.name;
    const meta = document.createElement('small');
    meta.textContent = `${String(theme.talkCount)} talks · guided path`;
    copy.append(name, meta);
    const arrow = document.createElement('span');
    arrow.className = 'thread-list__arrow';
    arrow.setAttribute('aria-hidden', 'true');
    arrow.textContent = '›';
    link.append(number, copy, arrow);
    item.append(link);
    list.append(item);
  }

  card.append(title, description, list);
  return card;
}

function createInsight(index: ConferenceIndex): HTMLElement {
  const topTheme = index.featuredThemes[0];
  const card = document.createElement('section');
  card.className = 'insight-card';
  card.setAttribute('aria-labelledby', 'insight-title');
  const label = document.createElement('p');
  label.className = 'eyebrow';
  label.textContent = 'Conference insight';
  const title = document.createElement('h2');
  title.id = 'insight-title';
  title.textContent = topTheme.name;
  const copy = document.createElement('p');
  copy.textContent =
    `${String(topTheme.talkCount)} talks develop this theme, making it the widest ` +
    'connection in the current analysis.';
  const link = routerLink(`/threads/${topTheme.slug}`, 'Follow this thread', 'text-link');
  card.append(label, title, copy, link);
  return card;
}

export function createHomePage(
  index: ConferenceIndex,
  readingProgress: ReadingProgress[] = [],
): HTMLElement {
  const page = document.createElement('div');
  page.className = 'home-page';
  page.dataset.route = 'home';
  page.append(createHero(index));

  const content = document.createElement('div');
  content.className = 'home-layout page-width';
  if (index.talks.length === 0) {
    const empty = document.createElement('p');
    empty.className = 'state-panel';
    empty.textContent = 'No conference talks are available yet.';
    content.append(empty);
  } else {
    const progressByTalk = new Map(
      readingProgress.map((progress) => [progress.talkId, progress]),
    );
    const continueProgress = readingProgress.find(
      (progress) => !progress.completed && progress.percent > 0,
    );
    const continueTalk =
      index.talks.find((talk) => talk.talkId === continueProgress?.talkId) ??
      index.talks[0];
    content.append(createContinueCard(continueTalk, continueProgress));
    const main = document.createElement('div');
    main.className = 'home-layout__main';
    main.append(createSessions(index, progressByTalk));
    const side = document.createElement('aside');
    side.className = 'home-layout__side';
    side.setAttribute('aria-label', 'Study starting points');
    side.append(createQuickThreads(index), createInsight(index));
    content.append(main, side);
  }
  page.append(content);
  return page;
}
